import { AccordionLink } from '@ksyos/design-system';
import { useNavigate } from '@ksyos/router';
import { useEffect, useState } from 'react';
import type { EpisodeCategory } from '../../../../common/enums/episodeCategory';
import type { IPatient } from '../../../../common/interfaces/patient';
import { getDisplayName } from '../../../../common/utils/getDisplayName';
import { fetchJson } from '../../../store/fetch';
import { translateKey } from '../../../translate';
import { addTrackEvent } from '../customControls/utils';
import { PatientAccessReasons } from './PatientAccessReasons';

export interface IPatientDownloadMedicalRecordOriginalProps {
    episodeCategory: EpisodeCategory;
    isFlexExport?: boolean;
    patientId: string;
}

export function PatientDownloadMedicalRecordOriginal(props: IPatientDownloadMedicalRecordOriginalProps) {
    const navigate = useNavigate();
    const [canRead, setCanRead] = useState(false);
    const [patient, setPatient] = useState<IPatient>();

    useEffect(() => {
        loadPatient();
    }, []);

    return (
        <>
            {canRead && patient && (
                <AccordionLink
                    onClick={downloadFile}
                    sx={{ mt: 1 }}
                    variant="button"
                >
                    {translateKey('patient.downloadMedicalRecord')}
                </AccordionLink>
            )}
            {!canRead && patient && (
                <PatientAccessReasons
                    onCancel={() => navigate(-1)}
                    onReasonGiven={() => setCanRead(true)}
                    patientId={props.patientId}
                />
            )}
        </>
    );

    async function loadPatient() {
        const patientId = props.patientId;
        const result = await fetchJson(
            `/api/patients/${patientId}?includeItemAuthorizations=canReadWithoutReason`,
            'GET'
        );

        setPatient(result.json);
        setCanRead(result.json.authorization.canReadWithoutReason);
    }

    function downloadFile() {
        addTrackEvent('Downloads', 'Download medical record', props.episodeCategory);

        let downloadUrl = '';

        if (props.isFlexExport) {
            downloadUrl = `/api/exportPdf/patients/${props.patientId}/episodeCategories/${props.episodeCategory}`;
        } else {
            const fileName = encodeURIComponent(
                `${getDisplayName(patient!)} - ${translateKey(`episodeCategoryEnum.${props.episodeCategory}`)}.pdf`
            );

            downloadUrl = `/api/makePdf/${props.patientId}/${props.episodeCategory}/${fileName}`;
        }

        window.open(downloadUrl, '_blank');
    }
}
